/*
 * ChatTurn — one message in the Orbit conversation feed.
 *
 * Three implicit variants by `role`:
 *   - orbit:  Orbit's question / acknowledgment, black ink, left-aligned
 *   - user:   user's text, soft blue card (#eef3ff), right-aligned
 *   - system: subtle inline status ("Filled 3 fields", "Skipped X"), centered
 *
 * Orbit messages may carry chips (the option suggestions for the current
 * question). User messages may carry an attachment label (URL / doc / voice).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Paperclip, Mic, Link as LinkIcon, ChevronRight, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

import type {
  DirectiveType, DirectivePayload, FieldOption, MarketplaceMatch,
} from '../lib/dossier-types';

import { Directive } from './directives/Directive';


export type TurnRole = 'orbit' | 'user' | 'system';


// ── LLM output rendering ──────────────────────────────────────────────
//
// Orbit's voice-skill prompt teaches the agent to use <em>...</em> for
// italic editorial accents (brand kit IBM Plex Serif italic). It also
// occasionally emits markdown (**bold**, lists, etc.). We:
//
//   1. Preprocess: convert the HTML whitelist (<em>, <i>, <strong>, <b>)
//      to markdown equivalents so everything flows through one renderer.
//   2. Render via react-markdown with GFM + line-break extensions.
//   3. Override the default block components (p, ul, ol, code) to feel
//      inline-native inside a chat bubble — no fat margins, no preformatted
//      gray blocks that break the visual rhythm.
//
// Result: agent can use either HTML accents or markdown freely; user sees
// clean italics, bold, lists, and code without raw tags ever leaking.

function preprocessLLMOutput(text: string): string {
  return text
    .replace(/<em>([\s\S]*?)<\/em>/gi, '*$1*')
    .replace(/<i>([\s\S]*?)<\/i>/gi, '*$1*')
    .replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**')
    .replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
    // Strip any other leaked tags as a safety net (defensive — no
    // innerHTML anywhere in this path).
    .replace(/<\/?[^>]+>/g, '');
}


const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p>{children}</p>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="aa-orbit-em" style={{ fontStyle: 'italic' }}>{children}</em>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong style={{ fontWeight: 600 }}>{children}</strong>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul style={{ margin: '0.4em 0', paddingLeft: '1.2em', listStyle: 'disc' }}>{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol style={{ margin: '0.4em 0', paddingLeft: '1.4em', listStyle: 'decimal' }}>{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li style={{ margin: '0.15em 0' }}>{children}</li>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code style={{
      padding: '1px 5px', borderRadius: 4, fontSize: '0.9em',
      background: '#f3f4f6', fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
    }}>{children}</code>
  ),
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#18191d', textDecoration: 'underline' }}
    >
      {children}
    </a>
  ),
};


const RichTurnText: React.FC<{ text: string }> = ({ text }) => {
  const cleaned = useMemo(() => preprocessLLMOutput(text || ''), [text]);
  const paragraphs = cleaned.split('\n\n').filter(Boolean);
  
  if (paragraphs.length > 1) {
    // Treat the first paragraph as the summary, and the rest as the question
    return (
      <div className="flex flex-col gap-4">
        <div className="text-[16.5px] font-normal leading-[1.55] tracking-[-0.003em] max-w-[56ch]">
          <ReactMarkdown
            components={markdownComponents as any}
            remarkPlugins={[remarkGfm, remarkBreaks]}
          >
            {paragraphs[0]}
          </ReactMarkdown>
        </div>
        <div className="text-[22px] font-medium leading-[1.4] tracking-[-0.012em] max-w-[38ch]">
          <ReactMarkdown
            components={markdownComponents as any}
            remarkPlugins={[remarkGfm, remarkBreaks]}
          >
            {paragraphs.slice(1).join('\n\n')}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  return (
    <ReactMarkdown
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      components={markdownComponents as any}
      remarkPlugins={[remarkGfm, remarkBreaks]}
    >
      {cleaned}
    </ReactMarkdown>
  );
};

export interface TurnAction {
  label: string;
  onClick: () => void;
  // Optional secondary line shown under the prompt above the button.
  detail?: string;
}

export interface ChatTurnProps {
  role: TurnRole;
  text: string;
  // Orbit-only: one-line rationale shown under the prompt (registry .why)
  why?: string | null;
  // Orbit-only: option chips inline below the prompt
  chips?: FieldOption[];
  // Receives the picked option's value AND label so the parent can show
  // the human-readable label in the chat while applying the raw value
  // to the dossier.
  onChipSelect?: (value: string, label: string) => void;
  // Orbit-only: triggered when the user submits a multiselect (clicks
  // "Done · N selected"). Values are the accumulated option values; the
  // parent commits the array via /confirm. Falls back to onChipSelect
  // for legacy single-pick chips.
  onMultiSubmit?: (values: string[]) => void;
  // Orbit-only: pre-selected option values for multiselect — e.g. when
  // the user already picked some, the chip-row reflects that.
  selectedValues?: string[];
  // Orbit-only: when true, chips toggle (multiselect mode) rather than
  // submit on click. A "Done · N selected" button replaces the implicit
  // commit-on-tap behaviour.
  multiselect?: boolean;
  // Orbit-only: a date question answers with the browser's own picker rather
  // than through the composer. A typed date is a date we cannot sort on, and
  // the backend refuses anything that is not a real calendar day, so the
  // composer would be a dead end here.
  datePicker?: boolean;
  dateValue?: string | null;
  onDateSubmit?: (iso: string) => void;
  // Orbit-only: show as the *active* question (subtle ring)
  active?: boolean;
  // Orbit-only: optional section tag rendered above the question
  // ("COMPANY · A"). Helps the user track which dossier section they're in.
  sectionTag?: string | null;
  // Slice 6 — intended respondent role for the active question. When
  // set, the byline renders a "CEO answers this" chip + a "Send to
  // CEO" button that fires onShareField with the field key.
  // Named respondentRole (not role) to avoid colliding with the turn-
  // role discriminator ('user' | 'orbit' | 'system') the props already use.
  respondentRole?: 'ceo' | 'cto' | 'cfo' | 'sponsor' | 'admin' | 'measurement_owner' | 'dept_lead' | null;
  /** Field dotted key for the role chip's share-to-role button. */
  roleFieldKey?: string | null;
  onShareField?: (fieldKey: string, role: string | null) => void;
  /** Schedule an Orbit voice call to the respondent role to fill this
   *  block (section-scoped). Renders a "Schedule a call" button beside
   *  "Send to [role]" when provided. */
  onScheduleField?: (fieldKey: string, role: string | null) => void;
  // Orbit-only: a primary branded CTA button rendered under the message.
  // Used by recommendation + post-pick action turns.
  action?: TurnAction | null;
  // Orbit-only: visual variant. 'recommend' adds a soft yellow editorial
  // glow; 'action' uses the green accent of confirmation.
  variant?: 'normal' | 'recommend' | 'action' | null;
  // Side-panel compact rendering for directive cards (heading + buttons).
  compact?: boolean;
  // User-only: tag for what kind of input this was (url / doc / voice)
  attachment?: 'url' | 'doc' | 'voice' | null;
  // System-only: severity affects color. `working` shows the loading card.
  systemKind?: 'info' | 'success' | 'warn' | 'working';
  // Orbit-only: v0.2 generative UI directive emitted by the agent's
  // `render` tool. When set, the matching primitive (ConfirmCard /
  // OfferResearchCard / RouteCard / ScalePicker) mounts below `why`
  // and ABOVE the chip row. `bubble` / `bubble_chips` / `bubble_text`
  // are passive — the existing chip row handles those.
  directiveType?: DirectiveType | null;
  directivePayload?: DirectivePayload | null;
  // Directive callbacks — fired by the right primitive. Parents wire
  // these into the same handlers that already deal with chip picks
  // (onChipSelect), URL paste (sendUrl), and approach commits.
  onDirectiveScan?: (url: string) => void;
  onDirectiveDecline?: () => void;
  onApproachPick?: (approachId: string) => void;
  onDirectiveScale?: (value: number) => void;
  /** Which approach is currently mid-write (RouteCard busy-state). */
  picking?: string | null;

  // ── v0.5 directive callbacks ───────────────────────────────────
  /** propose_prd_draft → user clicked "Draft the PRD". */
  onDraftPrd?: () => void;
  onEditBeforePrd?: () => void;
  onAddMoreBeforePrd?: () => void;
  drafting?: boolean;
  /** offer_paid_human → open the cal.com slot picker URL. */
  onOpenCalendar?: (url: string) => void;
  /** offer_paid_human → user picked a mock slot. Persisted via
   *  /api/intake/paid-human/book + confirmation chat turn. */
  onBookPaidHumanSlot?: (slot: {
    slotId: string;
    dateLabel: string;
    timeLabel: string;
    consultantName?: string;
    consultantRole?: string;
  }) => Promise<void> | void;
  onDismissPaidHuman?: () => void;
  onBackPaidHuman?: () => void;
  /** marketplace_matches → user picked a marketplace agent. */
  onPickMarketplace?: (slug: string, match: MarketplaceMatch) => void;
  onPickCustomBuild?: () => void;
  marketplacePicking?: 'marketplace' | 'custom' | null;
  // Slice 5 — enter_l2_self callbacks
  onEnterL2Continue?: () => void;
  onEnterL2TalkToPartner?: (payload: DirectivePayload) => void;
}

export function ChatTurn({
  role,
  text,
  why,
  chips,
  onChipSelect,
  onMultiSubmit,
  selectedValues,
  multiselect = false,
  datePicker = false,
  dateValue = null,
  onDateSubmit,
  active = false,
  sectionTag = null,
  respondentRole = null,
  roleFieldKey = null,
  onShareField,
  onScheduleField,
  action = null,
  variant = 'normal',
  compact = false,
  attachment = null,
  systemKind = 'info',
  directiveType = null,
  directivePayload = null,
  onDirectiveScan,
  onDirectiveDecline,
  onApproachPick,
  onDirectiveScale,
  picking = null,
  // v0.5
  onDraftPrd,
  onEditBeforePrd,
  onAddMoreBeforePrd,
  drafting = false,
  onOpenCalendar,
  onBookPaidHumanSlot,
  onDismissPaidHuman,
  onBackPaidHuman,
  onPickMarketplace,
  onPickCustomBuild,
  marketplacePicking = null,
  // Slice 5
  onEnterL2Continue,
  onEnterL2TalkToPartner,
}: ChatTurnProps) {
  // Multiselect local state — accumulates picks until user hits Done.
  // Seeded from selectedValues so a re-render (or returning from elsewhere)
  // doesn't wipe what they'd already chosen.
  const [pending, setPending] = useState<string[]>(() => selectedValues ?? []);
  // We map the "Explore" tab to "Map your business" if we are on /my-business, 
  // since Explore is not useful there, and Map Business is the primary context.
  const isMyBusiness = window.location.pathname.startsWith('/my-business');
  
  // Re-sync if parent's selectedValues changes (e.g. after a partial commit).
  useEffect(() => {
    if (multiselect && selectedValues) {
      setPending(selectedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiselect, (selectedValues || []).join(',')]);

  if (role === 'system') {
    return (
      <div className={`aa-turn aa-turn--system aa-turn--${systemKind}`}>
        <span className="aa-turn-system-dot" aria-hidden="true" />
        <span>{text}</span>
      </div>
    );
  }

  if (role === 'user') {
    return (
      <div className="aa-turn aa-turn--user">
        <div className="aa-turn-bubble aa-turn-bubble--user">
          {attachment === 'url' && <LinkIcon size={14} className="aa-turn-attach-ico" />}
          {attachment === 'doc' && <Paperclip size={14} className="aa-turn-attach-ico" />}
          {attachment === 'voice' && <Mic size={14} className="aa-turn-attach-ico" />}
          <span>{text}</span>
        </div>
      </div>
    );
  }

  // Orbit
  const variantClass =
    variant === 'recommend' ? ' aa-turn--recommend' :
    variant === 'action'    ? ' aa-turn--action' : '';

  // Slice 6 — role labels for the "[role] answers this" chip and the
  // "Send to [role]" button. Sentence case; no exclamations or emoji.
  const ROLE_LABELS: Record<string, string> = {
    ceo: 'CEO',
    cto: 'CTO',
    cfo: 'CFO',
    sponsor: 'Sponsor',
    admin: 'IT / Legal',
    measurement_owner: 'Measurement owner',
    dept_lead: 'Department lead',
  };
  const roleLabel = respondentRole ? (ROLE_LABELS[respondentRole] ?? null) : null;
  const canShare = Boolean(roleLabel && roleFieldKey && onShareField);
  const canSchedule = Boolean(roleLabel && roleFieldKey && onScheduleField);

  return (
    <div className={`aa-turn aa-turn--orbit${active ? ' aa-turn--active' : ''}${variantClass}`}>
      <div className="aa-turn-byline">
        Orbit
        {sectionTag && <span className="aa-turn-section-tag">{sectionTag}</span>}
        {roleLabel && (
          <span className="aa-turn-role-chip" aria-label={`${roleLabel} answers this`}>
            <span className="aa-turn-role-chip-dot" aria-hidden="true" />
            <span>{roleLabel} answers this</span>
          </span>
        )}
        {canShare && (
          <button
            type="button"
            className="aa-turn-share-btn"
            onClick={() => onShareField?.(roleFieldKey!, respondentRole)}
            aria-label={`Send to ${roleLabel}`}
            title={`Email a link so your ${roleLabel} fills this block`}
          >
            Send to {roleLabel}
          </button>
        )}
        {canSchedule && (
          <button
            type="button"
            className="aa-turn-share-btn"
            onClick={() => onScheduleField?.(roleFieldKey!, respondentRole)}
            aria-label={`Schedule a call with your ${roleLabel}`}
            title={`Orbit calls your ${roleLabel} and fills this block`}
          >
            Schedule a call
          </button>
        )}
      </div>
      <div className="aa-turn-bubble aa-turn-bubble--orbit">
        <RichTurnText text={text} />
      </div>
      {why && (
        <div className="aa-turn-why">
          <em>Why I ask.</em> {preprocessLLMOutput(why)}
        </div>
      )}
      {directiveType && directiveType !== 'bubble'
        && directiveType !== 'bubble_chips'
        && directiveType !== 'bubble_text' && (
        <Directive
          type={directiveType}
          payload={directivePayload}
          onAction={(value, label) => onChipSelect?.(value, label)}
          onScan={onDirectiveScan}
          onDecline={onDirectiveDecline}
          onApproachPick={onApproachPick}
          onScale={onDirectiveScale}
          picking={picking}
          compact={compact}
          // v0.5 — adaptive directives
          onDraftPrd={onDraftPrd}
          onEditBeforePrd={onEditBeforePrd}
          onAddMoreBeforePrd={onAddMoreBeforePrd}
          drafting={drafting}
          onOpenCalendar={onOpenCalendar}
          onBookPaidHumanSlot={onBookPaidHumanSlot}
          onDismissPaidHuman={onDismissPaidHuman}
          onBackPaidHuman={onBackPaidHuman}
          onPickMarketplace={onPickMarketplace}
          onPickCustomBuild={onPickCustomBuild}
          marketplacePicking={marketplacePicking}
          // Slice 5 — enter_l2 callbacks
          onEnterL2Continue={onEnterL2Continue}
          onEnterL2TalkToPartner={onEnterL2TalkToPartner}
        />
      )}
      {datePicker && (
        <DatePickerRow value={dateValue} onSubmit={onDateSubmit} />
      )}
      {chips && chips.length > 0 && (
        <>
          {multiselect && (
            <p className="aa-turn-multiselect-hint">
              Pick any that apply, then hit <em>Done</em>.
            </p>
          )}
          <div className="aa-turn-chiprow">
            {chips.map((o) => {
              const sub = (o as { subtitle?: string }).subtitle;
              const isAction = o.value.startsWith('__');
              const isSelected = multiselect && pending.includes(o.value);

              const handleClick = () => {
                if (isAction) {
                  // Action chips (e.g. "__skip_this") still commit
                  // immediately even inside a multiselect turn — skip
                  // shouldn't require a Done click.
                  onChipSelect?.(o.value, o.label);
                  return;
                }
                if (multiselect) {
                  // Toggle local selection state — actual commit happens
                  // when the user clicks "Done · N selected" below.
                  setPending((cur) =>
                    cur.includes(o.value)
                      ? cur.filter((v) => v !== o.value)
                      : [...cur, o.value],
                  );
                  return;
                }
                onChipSelect?.(o.value, o.label);
              };

              return (
                <button
                  key={o.value}
                  type="button"
                  className={
                    'aa-chip'
                    + (sub ? ' aa-chip--withsub' : '')
                    + (isAction ? ' aa-chip--action' : '')
                    + (isSelected ? ' aa-chip--on' : '')
                  }
                  data-value={o.value}
                  aria-pressed={multiselect ? isSelected : undefined}
                  onClick={handleClick}
                >
                  {isSelected && (
                    <Check size={12} strokeWidth={2.5} className="aa-chip-check" />
                  )}
                  <span className="aa-chip-label">{o.label}</span>
                  {sub && <span className="aa-chip-sub">{sub}</span>}
                </button>
              );
            })}
          </div>
          {multiselect && pending.filter(v => !v.startsWith('__')).length > 0 && (
            <div className="aa-turn-multiselect-submit">
              <button
                type="button"
                className="aa-multiselect-done"
                onClick={() => onMultiSubmit?.(pending.filter(v => !v.startsWith('__')))}
              >
                <span>
                  Done · {pending.filter(v => !v.startsWith('__')).length} selected
                </span>
                <ChevronRight size={14} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </>
      )}
      {action && (
        <div className="aa-turn-action-row">
          {action.detail && (
            <p className="aa-turn-action-detail">{action.detail}</p>
          )}
          <button
            type="button"
            className="aa-turn-action-btn"
            onClick={action.onClick}
          >
            <span>{action.label}</span>
            <ChevronRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}

/** The date answer control. Held local until it parses, so a half-picked day
 *  never reaches /confirm (which refuses it and would strand the turn). */
function DatePickerRow({
  value, onSubmit,
}: { value: string | null; onSubmit?: (iso: string) => void }) {
  const [draft, setDraft] = useState<string>(value ? value.slice(0, 10) : '');
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(draft);

  return (
    <div className="aa-turn-daterow">
      <input
        type="date"
        className="aa-turn-dateinput"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && valid) onSubmit?.(draft); }}
      />
      <button
        type="button"
        className="aa-multiselect-done"
        disabled={!valid}
        onClick={() => { if (valid) onSubmit?.(draft); }}
      >
        <span>Set date</span>
        <ChevronRight size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

export default ChatTurn;
